/* eslint-disable @typescript-eslint/no-use-before-define */

import { useCallback } from 'react';
import { useQuery } from '@apollo/client';

import asBentoModule from 'common/bento/hoc/asBentoModule';
import withStateMachine from 'common/bento/hoc/withStateMachine/withStateMachine';
import assertNever from 'common/bento/lib/assertNever';
import stringifyState from 'common/bento/lib/stringifyState';
import SuspenseQuery from 'components/SuspenseQuery';
import OBLoaderPage from 'features/Onboarding/components/OBLoaderPage/OBLoaderPage';
import { ApplicationType } from 'features/Onboarding/types';
import logger from 'helpers/logger/logger';

import { ComplianceModuleDataDocument } from './graphql/queries/complianceModuleData.gql';
import machineConfig, { model, State } from './machine';
import ComplianceForm from './pages/ComplianceForm';
import ComplianceRecap from './pages/ComplianceRecap/ComplianceRecap';
import ForeignTaxResidencyNotAllowed from './pages/ForeignTaxResidencyNotAllowed';
import TaxResidencies from './pages/TaxResidencies/TaxResidencies';
import TaxResidencyCountryNotAllowed from './pages/TaxResidencyCountryNotAllowed/TaxResidencyCountryNotAllowed';
import UsCitizenshipNotAllowed from './pages/UsCitizenshipNotAllowed';

interface ComplianceProps {
  applicationType: ApplicationType;
  companyProfileId?: string;
}

/**
 * Module that asks for user’s finances, FATCA and CRS.
 * And shows a blocker screen if some of their answers make them ineligible to proceed onboarding.
 */
const Compliance = asBentoModule<ComplianceProps>(
  ({ applicationType, companyProfileId, onDone }) => {
    const query = useQuery(ComplianceModuleDataDocument, {
      variables: {
        companyProfileId: companyProfileId || '',
        includeCompany: !!companyProfileId,
      },
    });

    return (
      <SuspenseQuery
        loader={<OBLoaderPage onPrev={() => null} />}
        query={query}
      >
        {(data) => (
          <ComplianceComponent
            moduleData={{ applicationType, complianceData: data }}
            onDone={onDone}
          />
        )}
      </SuspenseQuery>
    );
  },
);

const ComplianceComponent = withStateMachine<typeof model>(
  ({ machine, moduleData }) => {
    const [state, send] = machine;

    const stringifiedState = stringifyState<State>(state);

    const onPrev = useCallback(() => send(model.events.PREV()), [send]);

    const userProfile = moduleData.complianceData.viewer.profile;
    const {
      incomeRange,
      isComplianceFilled,
      isSpecifiedUSPerson,
      personalAssets,
    } = userProfile;

    switch (stringifiedState) {
      case State.FORM_PAGE:
      case State.SEND:
        return (
          <ComplianceForm
            initialValues={{
              incomeRange: incomeRange ?? '',
              isSpecifiedUSPerson: isSpecifiedUSPerson ?? undefined,
              personalAssets: personalAssets ?? undefined,
            }}
            isSubmitting={state.matches(State.SEND)}
            onPrev={onPrev}
            onSubmit={(values) =>
              send(model.events.SUBMIT_FISCAL_SITUATION(values))
            }
          />
        );

      case State.TAX_RESIDENCIES:
      case State.SEND_TAX_RESIDENCIES:
        return (
          <TaxResidencies
            isSaving={state.matches(State.SEND_TAX_RESIDENCIES)}
            onPrev={onPrev}
            onSubmit={(values) =>
              send(model.events.SAVE_TAX_RESIDENCIES(values))
            }
          />
        );

      case State.US_CITIZENSHIP_NOT_ALLOWED:
        return <UsCitizenshipNotAllowed onPrev={onPrev} />;

      case State.FOREIGN_TAX_RESIDENCY_NOT_ALLOWED:
        return <ForeignTaxResidencyNotAllowed onPrev={onPrev} />;

      case State.TAX_RESIDENCY_COUNTRY_NOT_ALLOWED:
        return <TaxResidencyCountryNotAllowed onPrev={onPrev} />;

      case State.RECAP: {
        if (!isComplianceFilled) {
          logger.error(
            `${State.RECAP} state reached but compliance info is not filled`,
          );
        }

        return (
          <ComplianceRecap
            complianceValues={{
              incomeRange: incomeRange ?? '',
              isSpecifiedUSPerson: isSpecifiedUSPerson ?? undefined,
              personalAssets: personalAssets ?? '',
            }}
            onNext={() => send(model.events.NEXT())}
            onPrev={onPrev}
            taxResidencies={moduleData.complianceData.viewer.taxResidencies}
          />
        );
      }

      case State.INIT:
      case State.ABORT:
      case State.DONE:
        return <OBLoaderPage onPrev={() => null} />;

      default:
        return assertNever(stringifiedState);
    }
  },
  {
    devTools: true,
    machineConfig,
  },
);

export default Compliance;
